import { User } from '@rematter/frontend-lib-api-client';
import { Badge, Col, Row, humanReadableDateTime } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { ChatEventDetail } from './chat';
import { ChatMessageEventDetail } from './chat-message';
import { outboxEventStatusColor } from './enumLabels';

import {
  $remoteAssistantOutboxEvents,
  useSelectedAssistantEventId,
} from '@/audit-log/_state/assistantEvents.state';
import { useTranslations } from '@/audit-log/i18n/useTranslations';
import { getLocale } from '~/shared/utils/locale';

const formatPayload = (payload: string) => {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
};

type MetadataRowProps = { label: string; value: string };

//
// ⚛️ ⚛️ ⚛️ METADATA ROW ⚛️ ⚛️ ⚛️
//
const MetadataRow = ({ label, value }: MetadataRowProps) => (
  <Row className="@MetadataRow justify-between py-1 text-sm">
    <span className="text-neutral/60">{label}</span>
    <span className="font-mono text-xs">{value}</span>
  </Row>
);

type AggregateType = 'Chat' | 'ChatMessage';

const getAggregateTypeFromEventName = (
  eventName: string,
): AggregateType | null => {
  if (eventName.startsWith('Chat') && eventName.includes('Message'))
    return 'ChatMessage';
  if (eventName.startsWith('Chat')) return 'Chat';
  return null;
};

type EventPayloadDetailProps = {
  aggregateType: AggregateType;
  payload: string;
};

//
// ⚛️ ⚛️ ⚛️ EVENT PAYLOAD DETAIL ⚛️ ⚛️ ⚛️
//
const EventPayloadDetail = ({
  aggregateType,
  payload,
}: EventPayloadDetailProps) => {
  switch (aggregateType) {
    case 'Chat':
      return <ChatEventDetail payload={payload} />;
    case 'ChatMessage':
      return <ChatMessageEventDetail payload={payload} />;
  }
};

//
// ⚛️ ⚛️ ⚛️ ASSISTANT EVENT DETAIL ⚛️ ⚛️ ⚛️
//
export const AssistantEventDetail = () => {
  const t = useTranslations();

  // Atoms
  const { data } = useAtomValue($remoteAssistantOutboxEvents);
  const [selectedEventId] = useSelectedAssistantEventId();

  const selectedEvent = data?.events.find((e) => e.id === selectedEventId);

  if (!selectedEvent) {
    return (
      <Col className="@AssistantEventDetail flex-1 items-center justify-center bg-neutral/2 text-neutral/40">
        <span>{t.assistantEventsView.detail.empty}</span>
      </Col>
    );
  }

  const aggregateType = getAggregateTypeFromEventName(selectedEvent.eventName);

  const selectedUser = selectedEvent.createdByUser as User;

  return (
    <Col className="@AssistantEventDetail flex-1 overflow-hidden bg-neutral/2">
      <Col className="flex-1 overflow-y-auto p-4" gap4>
        <Col gap2>
          <Row className="items-center justify-between">
            <span className="text-lg font-medium">
              {selectedEvent.eventName}
            </span>
            <Badge color={outboxEventStatusColor[selectedEvent.status]}>
              {selectedEvent.status}
            </Badge>
          </Row>
        </Col>

        <Col gap1 className="rounded-md border border-neutral/10 bg-white p-3">
          <span className="mb-2 text-sm font-medium text-neutral/80">
            {t.assistantEventsView.detail.metadata.title}
          </span>
          <MetadataRow
            label={t.assistantEventsView.detail.metadata.eventId}
            value={selectedEvent.id}
          />
          <MetadataRow
            label={t.assistantEventsView.detail.metadata.aggregateId}
            value={
              selectedEvent.metadata.aggregateId ??
              t.assistantEventsView.detail.notAvailable
            }
          />
          <MetadataRow
            label={t.assistantEventsView.detail.metadata.topic}
            value={selectedEvent.topic}
          />
          <MetadataRow
            label={t.assistantEventsView.detail.metadata.version}
            value={String(selectedEvent.version)}
          />
          <MetadataRow
            label={t.assistantEventsView.detail.metadata.retryCount}
            value={String(selectedEvent.retryCount)}
          />
          <MetadataRow
            label={t.assistantEventsView.detail.metadata.createdAt}
            value={humanReadableDateTime(selectedEvent.timestamps.createdAt, {
              locale: getLocale(),
              includeYear: true,
              includeSeconds: true,
            })}
          />
          <MetadataRow
            label={t.assistantEventsView.detail.metadata.createdBy}
            value={
              selectedUser
                ? `${selectedUser.firstName ?? ''} ${selectedUser.lastName ?? ''}`.trim()
                : t.assistantEventsView.detail.notAvailable
            }
          />
          {selectedEvent.lastRetry && (
            <MetadataRow
              label={t.assistantEventsView.detail.metadata.lastRetry}
              value={humanReadableDateTime(selectedEvent.lastRetry, {
                locale: getLocale(),
                includeYear: true,
                includeSeconds: true,
              })}
            />
          )}
        </Col>

        {aggregateType ? (
          <EventPayloadDetail
            aggregateType={aggregateType}
            payload={selectedEvent.payload as string}
          />
        ) : (
          <Col
            gap1
            className="flex-1 rounded-md border border-neutral/10 bg-white"
          >
            <span className="border-b border-neutral/10 p-3 text-sm font-medium text-neutral/80">
              {t.assistantEventsView.detail.payload}
            </span>
            <pre className="flex-1 overflow-auto p-3 font-mono text-xs">
              {formatPayload(selectedEvent.payload as string)}
            </pre>
          </Col>
        )}
      </Col>
    </Col>
  );
};
